import { useContext } from 'react'
import { Navigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useRoles } from 'app/components/settings-roles/useRoles'
import { RolesShadcnComponent, type RoleRow } from 'app/components/settings-roles/RolesShadcnComponent'
import type { RoleJson } from 'app/types/entities'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { UserPermissionsContext } from 'app/hooks/useUserPermission'

export default observer(function Roles(): React.ReactElement | null {
  const checkPermission = useCheckPermission()
  const { permissions } = useContext(UserPermissionsContext)
  const canManageRoles = checkPermission([`org:settings:manage-roles`, `roles:manage`])
  // Every hook runs before any conditional return. useCheckPermission resolves
  // asynchronously (permissions read `undefined` until the session loads, and
  // are replaced again on a workspace switch, which can drop manage-roles), so
  // gating useRoles behind the early <Navigate> changed the hook count between
  // renders and crashed the route with "rendered fewer hooks than expected" —
  // a blank page, since nothing above the route catches it (TRK-5214).
  const { sortedRoles, defaultRoleNames, openCreateModal, openEditModal } = useRoles()

  // Permissions read `undefined` until they arrive, which makes every check
  // false. Redirecting on that premature false ejects a user who does have
  // access (TRK-5546).
  if (permissions === undefined) return null

  if (!canManageRoles) return <Navigate to="/settings" replace />

  return (
    <RolesShadcnComponent
      roles={sortedRoles?.hits}
      canManageRoles={canManageRoles}
      defaultRoleNames={defaultRoleNames}
      onCreateRole={openCreateModal}
      onEditRole={(role: RoleRow) => openEditModal(role as RoleJson)}
    />
  )
})
